// src/services/aeo/baselineSnapshot.ts
//
// The Baseline Snapshot (ourteam-backend#6203): the table of the first AI
// check's 50 questions, what ChatGPT and Gemini said and whom they named
// instead, made by Oura on the server from a finished scan. It replaces the
// one built by hand in Claude Desktop; staff read it, then upload the baseline
// report the results email carries.
//
//   GET  /assessments/{id}/baseline-snapshot  -> { status, at, by, data, error }
//   POST /assessments/{id}/baseline-snapshot  -> 202 { status: "making" }
//
// Made automatically when the schedule is published (ourteam-backend#6205),
// or by hand with the POST. Always the live routes: there is no sample store
// and no switch, the routes are live in staging and production.
//
// Everything in `data` is text to draw as it is: the backend writes every
// sentence, header and count, so nothing here is worked out on the frontend.

import axios from "axios";
import { getCookie } from "@util/cookie";
import { catchErrorLog } from "@util/sentry";

const AEO_BASE = `${import.meta.env.VITE_TALENT_AI_BE_BASE_URL}/api/v1`;

function authHeaders(): Record<string, string> {
    const token = getCookie("authToken");
    return token ? { Authorization: `Bearer ${token}` } : {};
}

export type SnapshotStatus = "not_made" | "making" | "ready" | "failed";

/**
 * One engine's answer to one question. `available: false` is "not read": that
 * engine has no answer stored for the question. It is drawn greyed, never as
 * a "no", and the totals already leave it out (ourteam-backend#6203).
 */
export interface SnapshotCell {
    named?: boolean;
    rank?: number | null;
    /** "YES, ranked #1", "YES, #3", "YES", "no", or "not read". */
    label: string;
    available?: boolean;
}

export interface SnapshotRow {
    n: number;
    question: string;
    check: string;
    group?: string | null;
    /** A buyer-intent question with a location: drawn with a star. */
    local: boolean;
    /** Keyed by engine key (`chatgpt_app`, `gemini`). */
    cells: Record<string, SnapshotCell>;
    rivals: string[];
    hit: boolean;
    /** False: out of the total, drawn greyed. */
    counted: boolean;
    /** No assistant named anyone on this question. */
    nobodyNamed: boolean;
}

export interface SnapshotCheck {
    check: string;
    title: string;
    question: string;
    questions: number;
    namedQuestions: number;
    named: number;
    of: number;
    pct: number;
}

/**
 * The simplified top (Jack, 20 Oct 2026): one headline in QUESTIONS, the same
 * count as the Visibility page (`aiVisibility.questionsNamed` of `questionsOf`),
 * with each engine's count and the rival named most underneath. Present: the
 * drawer draws this and the check cards in questions only, and drops the four
 * stat cards. Absent: the older layout, unchanged.
 */
export interface SnapshotHeadline {
    /** "20 of 50 questions". */
    value: string;
    /** "name Brickhouse by Santai Villas". */
    label: string;
    /** "We asked AI 50 questions a customer would ask." */
    intro?: string | null;
    /** "ChatGPT 6 · Gemini 17 · Google AI 2". */
    engines?: string | null;
    /** "Named most instead: Embun Luxury Villas, on 30 questions", or null when nobody was. */
    rival?: string | null;
}

export interface BaselineSnapshot {
    headline?: SnapshotHeadline | null;
    version?: string;
    assessmentId: string;
    business: { name: string; domain: string; market?: string | null; category?: string | null; noun?: string | null };
    date: string;
    title: string;
    /** The header line above the title. */
    meta?: string | null;
    footer?: string | null;
    /** The table's column headers, as the backend names them. */
    columns?: string[] | null;
    engines: { key: string; label: string }[];
    stats: { value: string; label: string }[];
    checks: SnapshotCheck[];
    checkBand?: { heading?: string | null; legend?: string[] | null } | null;
    lead?: string | null;
    callout?: string | null;
    notesHeading?: string | null;
    notes: string[];
    /** The star sentence under the table. */
    legendText?: string | null;
    rows: SnapshotRow[];
    legend?: Record<string, number> | null;
}

export interface SnapshotRead {
    status: SnapshotStatus;
    at: string | null;
    by: string | null;
    data: BaselineSnapshot | null;
    /** `failed` only: the reason, e.g. "no stored answers for Gemini". */
    error: string | null;
}

export type SnapshotResult = { ok: true; read: SnapshotRead } | { ok: false; message: string };

const STATUSES: readonly SnapshotStatus[] = ["not_made", "making", "ready", "failed"];

function isRecord(value: unknown): value is Record<string, unknown> {
    return value !== null && typeof value === "object" && !Array.isArray(value);
}

/** Enough of the report to draw it: the parts the drawer reads without a fallback. */
function isBaselineSnapshot(value: unknown): value is BaselineSnapshot {
    if (!isRecord(value) || !isRecord(value.business)) return false;
    return typeof value.assessmentId === "string" && typeof value.title === "string" && typeof value.date === "string"
        && typeof value.business.name === "string" && typeof value.business.domain === "string"
        && Array.isArray(value.engines) && Array.isArray(value.stats) && Array.isArray(value.checks)
        && Array.isArray(value.notes) && Array.isArray(value.rows);
}

/**
 * The server's read. An unknown status, or "ready" without a report to draw, is NOT "not made": that would
 * offer "Make it now" (a write) on a state nobody knows. It throws, so the caller shows a read error and
 * Try again instead (absent means unknown, never false).
 */
function readOf(raw: unknown): SnapshotRead {
    if (!isRecord(raw)) throw new Error("Invalid baseline snapshot response");
    const rawStatus = raw.status;
    if (typeof rawStatus !== "string" || !STATUSES.includes(rawStatus as SnapshotStatus)) {
        throw new Error(`Unknown baseline snapshot status: ${String(rawStatus)}`);
    }
    const status = rawStatus as SnapshotStatus;
    if (status === "ready" && !isBaselineSnapshot(raw.data)) throw new Error("Baseline snapshot is ready without a report to draw");
    return {
        status,
        at: typeof raw.at === "string" ? raw.at : null,
        by: typeof raw.by === "string" ? raw.by : null,
        data: status === "ready" ? (raw.data as BaselineSnapshot) : null,
        error: typeof raw.error === "string" && raw.error.trim() ? raw.error : null,
    };
}

function refusal(err: unknown, fallback: string, what: string): SnapshotResult {
    const e = err as { response?: { status?: number; data?: { message?: unknown } } };
    const message = typeof e.response?.data?.message === "string" && e.response.data.message.trim() ? e.response.data.message : null;
    if (!message) catchErrorLog(`[oura] baseline snapshot ${what} failed: ${err}`);
    return { ok: false, message: message ?? fallback };
}

/** Where the snapshot stands for this scan, and the report once it is ready. */
export async function getBaselineSnapshot(assessmentId: string): Promise<SnapshotResult> {
    try {
        const res = await axios.get(`${AEO_BASE}/assessments/${encodeURIComponent(assessmentId)}/baseline-snapshot`, { headers: authHeaders() });
        return { ok: true, read: readOf(res.data?.data) };
    } catch (err) {
        return refusal(err, "The Baseline Snapshot could not be read. Try again in a moment.", "read");
    }
}

/** Ask Oura to make it now (or again). It is built in the background: read it again until it is ready. */
export async function makeBaselineSnapshot(assessmentId: string): Promise<SnapshotResult> {
    try {
        const res = await axios.post(`${AEO_BASE}/assessments/${encodeURIComponent(assessmentId)}/baseline-snapshot`, {}, { headers: authHeaders() });
        return { ok: true, read: readOf({ status: "making", ...(res.data?.data ?? {}) }) };
    } catch (err) {
        return refusal(err, "Oura could not start the Baseline Snapshot. Try again in a moment.", "make");
    }
}
